import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback, useMemo, useState } from "react";
import { FinancialDocumentListItem } from "@common/types/financialDocument";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Picker } from "@design-system/Picker/Picker.lib";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { useEntities } from "@hooks/useEntities";
import { useChangeEntityMutation } from "@mutations/financialDocument";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { PickerEntityItem } from "@components/entity/PickerEntityItem";
import { getDocumentLockedTooltip } from "../../lib";
import { useIsFinancialDocumentsTableReadonly } from "../useIsFinancialDocumentsTableReadonly";

export const EntityCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, FinancialDocumentListItem, unknown>) => {
        const document = row.original;
        const isTableReadonly = useIsFinancialDocumentsTableReadonly(table);
        const entities = useEntities();
        const entity = document.entity;
        const handleChangeEntity = useChangeEntityMutation();
        const [isOpen, setIsOpen] = useState(false);
        const hasMultipleEntities = entities.length > 1;
        const lockedTooltip = getDocumentLockedTooltip(
            document,
            document.integrationAccount,
        );
        const readonlyTooltip = useViewOnlyTooltipReason(lockedTooltip ?? "");

        const blurActiveElement = useCallback(() => {
            const activeElement = window.document.activeElement;
            if (activeElement instanceof HTMLElement) {
                activeElement.blur();
            }
        }, []);

        const closeAndBlur = useCallback(() => {
            setIsOpen(false);
            requestAnimationFrame(() => {
                blurActiveElement();
            });
        }, [blurActiveElement]);

        const handleEntityChange = useCallback(
            (entityId: number | null) => {
                if (entityId === null || entityId === entity?.id) {
                    closeAndBlur();
                    return;
                }
                const selected = entities.find((e) => e.id === entityId);
                if (!selected) {
                    return;
                }
                handleChangeEntity.mutate({
                    financialDocument: document,
                    newEntity: selected,
                });
                closeAndBlur();
            },
            [entity?.id, entities, handleChangeEntity, document, closeAndBlur],
        );

        const triggerContent = useMemo(
            () => (entity ? <EntityAvatar entity={entity} size="xs" /> : null),
            [entity],
        );

        const trigger = useMemo(
            () => (
                <Picker.CellContent
                    addText=""
                    value={triggerContent ?? undefined}
                    aria-label={entity?.name ?? undefined}
                />
            ),
            [entity, triggerContent],
        );

        if (!hasMultipleEntities || readonlyTooltip || isTableReadonly) {
            return (
                <ViewTableCell captureClicks>
                    {entity ? (
                        <EditableCellContent
                            truncationFullText={entity.name ?? undefined}
                            readonly
                            tooltip={readonlyTooltip || undefined}
                            addText=""
                        >
                            {triggerContent}
                        </EditableCellContent>
                    ) : null}
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks>
                <Picker.Select
                    value={entity?.id ?? null}
                    onValueChange={handleEntityChange}
                    open={isOpen}
                    onOpenChange={setIsOpen}
                    chevron="none"
                >
                    {trigger}
                    <Picker.Popup>
                        <Picker.List>
                            {entities.map((e) => (
                                <PickerEntityItem
                                    key={e.id}
                                    value={e.id}
                                    entity={e}
                                />
                            ))}
                        </Picker.List>
                    </Picker.Popup>
                </Picker.Select>
            </ViewTableCell>
        );
    },
);
EntityCell.displayName = "EntityCell";
